import { PayloadAction } from "@reduxjs/toolkit";
import { all, call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { getPaginationMeta } from "utils/helpers/pagination";
import { request } from "utils/request";

import {
  NeedEngPlan,
  RegistrationForm,
  RegistrationFormDropDownKey,
  RegistrationListResponse,
  RegistrationUpdate,
} from "../types";
import {
  selectRegistrationForm,
  selectRegistrationFormDropDowns,
  selectRegistrationPage,
  selectRegistrationPerPage,
} from "./selector";
import { registrationActions } from "./slice";

let isFetchingList = false;

const DROP_DOWN_PATH: Record<RegistrationFormDropDownKey, string> = {
  tires: "tire-type/list",
  bodyTypes: "body-type/list",
};

export function* handleGetRegistrationList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const page: number = yield select(selectRegistrationPage);
    const perPage: number = yield select(selectRegistrationPerPage);

    const response: RegistrationListResponse = yield call(
      request,
      `${API_URL}/registration-instructions?page=${page}&perPage=${perPage}`,
      { method: "GET" }
    );

    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { data, meta };

    if (page === 1) {
      yield put(registrationActions.getRegistrationListSuccess(payload));
    } else {
      yield put(registrationActions.getNextPageItemsSuccess(payload));
    }
  } catch (error) {
    const message = (error as Error).message;
    const page: number = yield select(selectRegistrationPage);

    if (page === 1) {
      yield put(registrationActions.getRegistrationListError(message));
    } else {
      yield put(registrationActions.getNextPageItemsError(message));
    }
  } finally {
    isFetchingList = false;
  }
}

export function* handleAddRegistration() {
  const form: RegistrationForm = yield select(selectRegistrationForm);

  try {
    yield call(request, `${API_URL}/registration-instructions`, {
      method: "POST",
      body: JSON.stringify({
        manufecturerID: 1,
        modelID: 593,
        year: 2024,
        cabinType: form.driverCabinType.trim(),
        tireSize: form.tire.trim(),
        hangerType: form.suspensionType.trim(),
        weight: form.weight.trim(),
        wealBase: Number(form.axlesMeasure),
        lowerRange: Number(form.lowerLimit),
        upperRange: Number(form.upperLimit),
        axleLoad: Number(form.frontLoad),
        backAxleDistance: Number(form.rearAxlesMeasure),
        remarks: form.notes.trim(),
        needEngPlan: NeedEngPlan.No,
        sideWallTypeID: Number(form.bodyType),
        structureType: Number(form.dhType),
      }),
    });

    yield put(registrationActions.addRegistrationSuccess());
    yield put(registrationActions.getRegistrationList());
  } catch (error) {
    yield put(
      registrationActions.addRegistrationError((error as Error).message)
    );
  }
}

export function* handleUpdateRegistration(
  action: PayloadAction<RegistrationUpdate>,
) {
  const { id, ...body } = action.payload;

  try {
    yield call(request, `${API_URL}/registration-instructions/${id}`, {
      method: "PATCH",
      body: JSON.stringify({
        manufacturerID: Number(body.manufacturerID),
        modelID: Number(body.modelID),
        year: Number(body.year),
        cabinType: body.cabinType.trim(),
        tireSize: body.tireSize.trim(),
        hangerType: body.hangerType.trim(),
        weight: body.weight.trim(),
        wealBase: Number(body.wealBase),
        lowerRange: Number(body.lowerRange),
        upperRange: Number(body.upperRange),
        axleLoad: Number(body.axleLoad),
        backAxleDistance: Number(body.backAxleDistance),
        remarks: body.remarks.trim(),
        needEngPlan: body.needEngPlan,
        sideWallTypeID: Number(body.sideWallTypeID),
        structureType: Number(body.structureType),
      }),
    });

    yield put(registrationActions.updateRegistrationSuccess());
    yield put(registrationActions.getRegistrationList());
  } catch (error) {
    yield put(
      registrationActions.updateRegistrationError((error as Error).message),
    );
  }
}

export function* handleDeleteRegistration(action: PayloadAction<number>) {
  const id = action.payload;

  try {
    yield call(request, `${API_URL}/registration-instructions/${id}`, {
      method: "DELETE",
    });

    yield put(registrationActions.deleteRegistrationSuccess(id));
  } catch (error) {
    yield put(
      registrationActions.deleteRegistrationError((error as Error).message),
    );
  }
}

function* fetchDropDown(key: RegistrationFormDropDownKey) {
  try {
    const formDropDowns = yield select(selectRegistrationFormDropDowns);
    const { page, perPage } = formDropDowns[key];
    const response = yield call(
      request,
      `${API_URL}/${DROP_DOWN_PATH[key]}?page=${page}&perPage=${perPage}`,
      { method: "GET" }
    );
    const data = response?.data || [];
    const meta = getPaginationMeta(page, perPage, data, response?.meta);
    const payload = { key, data, meta };

    yield put(
      page === 1
        ? registrationActions.getFormDropDownListSuccess(payload)
        : registrationActions.getFormDropDownNextPageSuccess(payload)
    );
  } catch (error) {
    const message = (error as Error).message;
    const formDropDowns = yield select(selectRegistrationFormDropDowns);

    yield put(
      formDropDowns[key].page === 1
        ? registrationActions.getFormDropDownListError({ key, message })
        : registrationActions.getFormDropDownNextPageError({ key, message })
    );
  }
}

export function* handleGetFormDropDownList() {
  yield all([call(fetchDropDown, "tires"), call(fetchDropDown, "bodyTypes")]);
}

export function* handleGetFormDropDownNextPage(
  action: PayloadAction<RegistrationFormDropDownKey>
) {
  yield call(fetchDropDown, action.payload);
}

export function* registrationSaga() {
  yield takeLeading(
    registrationActions.getRegistrationList.type,
    handleGetRegistrationList
  );
  yield takeLeading(
    registrationActions.getNextPageItems.type,
    handleGetRegistrationList
  );
  yield takeLatest(
    registrationActions.addRegistration.type,
    handleAddRegistration
  );
  yield takeLatest(
    registrationActions.updateRegistration.type,
    handleUpdateRegistration,
  );
  yield takeLatest(
    registrationActions.deleteRegistration.type,
    handleDeleteRegistration,
  );
  yield takeLatest(
    registrationActions.getFormDropDownList.type,
    handleGetFormDropDownList
  );
  yield takeLatest(
    registrationActions.getFormDropDownNextPage.type,
    handleGetFormDropDownNextPage
  );
}

export default registrationSaga;
